// "Add to plan" on the issue page's Plans row, and the picker it opens (today's
// plans/AddToPlan.tsx and PlanPicker.tsx): an open plan that does not list
// the issue yet, its own project's first, current ones first, and one of its phases or a new one she
// names. The add is `list_in_plan`, through the store's outbox: the plan
// shows among the row's plans at once. The plans come from the store; a
// plan's phases from its body, which the store reads while the picker shows
// it. Today's suggested plan, which a provider of this Mac's Code tab made,
// is left out.
//
// "Change" beside each plan that lists the issue opens the same fields for
// that plan: its phases, the issue's own chosen, to move it to another
// (`move_in_plan`), or to take it out (`unlist_from_plan`).
import { useEffect, useMemo, useRef, useState } from 'react'
import type { Issue, PlanRow } from '../api/types'
import { PlusIcon } from '../components/Icons'
import { Working } from '../components/Working'
import { ErrorState } from '../components/layout'
import { useBoardState, useStore } from '../store/context'
import { CURRENT_PLAN_STATUSES } from '../vocab'
import { PHASE_NAME_MAX, planPhases } from './listing'
import { comparePlans } from './state'
import { placeWords as words } from './words'

export function AddToPlan({ issue }: { issue: Issue }) {
  const [open, setOpen] = useState(false)
  const opener = useRef<HTMLButtonElement>(null)
  const close = () => {
    setOpen(false)
    opener.current?.focus()
  }
  return (
    <>
      <button ref={opener} type="button" className="button button-small button-quiet plan-add" aria-expanded={open} onClick={() => setOpen((was) => !was)}>
        <PlusIcon size={12} /> {words.addToPlan}
      </button>
      {open && <PlanPicker issue={issue} onAdded={close} onCancel={close} />}
    </>
  )
}

/** Plans still open: neither completed nor cancelled. */
const OPEN = (plan: PlanRow) => plan.status !== 'completed' && plan.status !== 'cancelled'

/** Current plans first (In Progress, then Planned), then the rest, each in the plans' order. */
function rank(plan: PlanRow): number {
  const at = CURRENT_PLAN_STATUSES.indexOf(plan.status)
  return at < 0 ? CURRENT_PLAN_STATUSES.length : at
}

export function PlanPicker({ issue, onAdded, onCancel }: { issue: Issue; onAdded: () => void; onCancel: () => void }) {
  const store = useStore()
  const { plans, planDetails, planErrors } = useBoardState()
  const [planId, setPlanId] = useState<string | null>(null)
  // The index of the phase chosen; one past the last is "New phase…".
  const [phaseAt, setPhaseAt] = useState(0)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  const open = useMemo(
    () => [...plans.values()].filter((plan) => OPEN(plan) && !plan.issues.some((listing) => listing.key === issue.key)).sort((a, b) => Number(a.project !== issue.project) - Number(b.project !== issue.project) || rank(a) - rank(b) || comparePlans(a, b)),
    [plans, issue.key, issue.project],
  )
  const plan = open.find((one) => one.id === planId) ?? open[0]
  useEffect(() => (plan ? store.watchPlan(plan.id) : undefined), [store, plan?.id])
  const body = plan ? planDetails.get(plan.id)?.body : undefined
  const phases = useMemo(() => (body === undefined ? null : planPhases(body)), [body])
  const choice = Math.min(phaseAt, phases?.length ?? 0)
  const fresh = phases !== null && choice === phases.length
  const typed = name.trim()

  const submit = async () => {
    if (!plan || !phases || busy || (fresh && !typed)) return
    setBusy(true)
    const added = await store.listInPlan(plan.id, issue.id, fresh ? typed : phases[choice])
    setBusy(false)
    if (added) onAdded()
  }

  return (
    <div
      className="plan-picker"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          onCancel()
        }
      }}
    >
      {open.length === 0 && <p className="muted">{words.noPlans}</p>}
      {plan && (
        <div className="plan-picker-fields">
          <select aria-label={words.plan} value={plan.id} disabled={busy} onChange={(event) => { setPlanId(event.target.value); setPhaseAt(0) }}>
            {open.map((one) => (
              <option key={one.id} value={one.id}>
                {one.title}
              </option>
            ))}
          </select>
          {phases === null ? (
            planErrors.has(plan.id) ? <ErrorState error={planErrors.get(plan.id)!} onRetry={() => void store.refreshPlan(plan.id)} /> : <p className="muted"><Working visibleLabel label={words.reading} /></p>
          ) : (
            <select aria-label={words.phase} value={choice} disabled={busy} onChange={(event) => setPhaseAt(Number(event.target.value))}>
              {phases.map((phase, at) => (
                <option key={`${at}:${phase}`} value={at}>
                  {phase}
                </option>
              ))}
              <option value={phases.length}>{words.newPhase}</option>
            </select>
          )}
          {fresh && (
            <input
              aria-label={words.phaseName}
              value={name}
              maxLength={PHASE_NAME_MAX}
              disabled={busy}
              autoFocus
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  void submit()
                }
              }}
            />
          )}
        </div>
      )}
      <div className="plan-picker-actions">
        <button type="button" className="button button-small" disabled={!plan || !phases || busy || (fresh && !typed)} onClick={() => void submit()}>
          {words.add}
        </button>
        <button type="button" className="button button-small button-quiet" onClick={onCancel}>
          {words.cancel}
        </button>
      </div>
    </div>
  )
}

/** "Change" beside a plan that lists the issue, and the fields it opens. */
export function ChangePlace({ issue, plan }: { issue: Issue; plan: PlanRow }) {
  const [open, setOpen] = useState(false)
  const opener = useRef<HTMLButtonElement>(null)
  const close = () => {
    setOpen(false)
    opener.current?.focus()
  }
  return (
    <>
      <button ref={opener} type="button" className="button button-small button-quiet" aria-expanded={open} aria-label={words.changeIn(plan.title)} onClick={() => setOpen((was) => !was)}>
        {words.change}
      </button>
      {open && <PlaceFields issue={issue} plan={plan} onDone={close} />}
    </>
  )
}

function PlaceFields({ issue, plan, onDone }: { issue: Issue; plan: PlanRow; onDone: () => void }) {
  const store = useStore()
  const { planDetails, planErrors } = useBoardState()
  const current = plan.issues.find((listing) => listing.key === issue.key)?.section ?? null
  useEffect(() => store.watchPlan(plan.id), [store, plan.id])
  const body = planDetails.get(plan.id)?.body
  const phases = useMemo(() => (body === undefined ? null : planPhases(body)), [body])
  // The index of the phase chosen, the issue's own at first; one past the last is "New phase…".
  const [phaseAt, setPhaseAt] = useState<number | null>(null)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const own = phases && current !== null ? phases.indexOf(current) : -1
  const choice = Math.min(phaseAt ?? Math.max(own, 0), phases?.length ?? 0)
  const fresh = phases !== null && choice === phases.length
  const typed = name.trim()
  const canMove = phases !== null && !busy && (fresh ? typed !== '' : choice !== own)

  const move = async () => {
    if (!phases || !canMove) return
    setBusy(true)
    const done = await store.moveInPlan(plan.id, issue.id, fresh ? typed : phases[choice]!)
    setBusy(false)
    if (done) onDone()
  }
  const remove = async () => {
    if (busy) return
    setBusy(true)
    const done = await store.unlistFromPlan(plan.id, issue.id)
    setBusy(false)
    if (done) onDone()
  }

  return (
    <div
      className="plan-picker"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          onDone()
        }
      }}
    >
      <div className="plan-picker-fields">
        {phases === null ? (
          planErrors.has(plan.id) ? <ErrorState error={planErrors.get(plan.id)!} onRetry={() => void store.refreshPlan(plan.id)} /> : <p className="muted"><Working visibleLabel label={words.reading} /></p>
        ) : (
          <select aria-label={words.phase} value={choice} disabled={busy} onChange={(event) => setPhaseAt(Number(event.target.value))}>
            {phases.map((phase, at) => (
              <option key={`${at}:${phase}`} value={at}>
                {phase}
              </option>
            ))}
            <option value={phases.length}>{words.newPhase}</option>
          </select>
        )}
        {fresh && (
          <input
            aria-label={words.phaseName}
            value={name}
            maxLength={PHASE_NAME_MAX}
            disabled={busy}
            autoFocus
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                void move()
              }
            }}
          />
        )}
      </div>
      <div className="plan-picker-actions">
        <button type="button" className="button button-small" disabled={!canMove} onClick={() => void move()}>
          {words.move}
        </button>
        <button type="button" className="button button-small button-quiet" disabled={busy} onClick={() => void remove()}>
          {words.remove}
        </button>
        <button type="button" className="button button-small button-quiet" onClick={onDone}>
          {words.cancel}
        </button>
      </div>
    </div>
  )
}
